/**
 * ChatScreen - Full-featured chat with Genesis Router
 *
 * Uses the shared components for consistent UI and the Chat component
 * for all the backend integration (WebSocket, SSE, multimodal).
 * 
 * Supports:
 * - New conversations via ?message= parameter
 * - Resuming existing conversations via ?conversationId= parameter
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { Chat } from '../components/Chat';
import { SpaceBackground, Header, Sidebar, SettingsPanel } from '../components/shared';
import { LoginScreen } from '../components/LoginScreen';

export default function ChatScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading, signOut } = useAuthContext();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Get initial message from URL params (from intent bubbles or chatbar)
  const initialMessage = searchParams.get('message') || '';

  // Get conversation ID for resuming existing conversations
  const conversationId = searchParams.get('conversationId') || undefined;

  // Get execution mode from URL params (fast, super)
  const executionMode = (searchParams.get('mode') as 'fast' | 'super') || 'super';

  // Handle sign out - navigate back to home
  const handleSignOut = async () => {
    navigate('/');
  };

  // Show loading state
  if (loading) {
    return (
      <div className="relative w-full h-screen overflow-hidden bg-black text-cyan-400 font-sans">
        <SpaceBackground />
        <div className="relative z-10 flex items-center justify-center h-full">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-12 h-12 border-4 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
            <p className="text-cyan-400/70 text-sm tracking-wider">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!user) {
    return <LoginScreen />;
  }

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black text-cyan-400 font-sans selection:bg-cyan-400/20 selection:text-cyan-100">
      <SpaceBackground />

      <div className={`relative z-10 flex flex-col h-full transition-transform duration-500 ${sidebarOpen ? 'translate-x-[320px] scale-95 opacity-50 blur-[2px]' : ''}`}>
        <Header onMenuClick={() => setSidebarOpen(true)} />

        {/* Chat Component - fills remaining space below header */}
        <div className="flex-1 mt-20 overflow-hidden">
          <Chat
            user={user}
            onSignOut={handleSignOut}
            initialMessage={initialMessage}
            conversationId={conversationId}
            executionMode={executionMode}
          />
        </div>
      </div>

      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onSettingsClick={() => { setSidebarOpen(false); setSettingsOpen(true); }} userName={user?.displayName || user?.email?.split("@")[0]} userPhoto={user?.photoURL}
      />
      <SettingsPanel isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} onSignOut={signOut} />
    </div>
  );
}
